// import removed: isAV1Supported
'use client'

import videos from '@/data/videos'
import type VideoChapter from '@/types/VideoChapter'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { useControl } from './ControlContext'
import { useSubtitle } from './SubtitleContext'
import { useUserPrefs } from './UserPrefsContext'
import { useVideo } from './VideoContext'

interface ChapterState {
  currentChapter: VideoChapter | undefined
  currentChapterNumber: number
  totalChapters: number
  showChapterMenu: boolean
  isChangingChapter: boolean // Indicates if a chapter change is in progress
  showHotspots: boolean
}

interface ChapterContextType extends ChapterState {
  getChapter: (chapter: number) => VideoChapter | undefined
  getCurrentChapter: () => VideoChapter | undefined
  setCurrentChapter: (chapter: number, autoPlay?: boolean) => void
  nextChapter: () => void
  previousChapter: () => void
  setShowChapterMenu: (show?: boolean, resumeVideo?: boolean) => void
  setIsChangingChapter: (changing: boolean) => void
  setShowHotspots: (show: boolean) => void
}

const initialChapterState: ChapterState = {
  currentChapter: undefined,
  currentChapterNumber: 0,
  totalChapters: videos.length,
  showChapterMenu: false,
  isChangingChapter: false,
  showHotspots: false,
}

const ChapterContext = createContext<ChapterContextType | undefined>(undefined)
export function ChapterProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ChapterState>(initialChapterState)
  const { userPrefs } = useUserPrefs()
  const { setCurrentVideo } = useVideo()
  const { setSubtitleFile, setEnabled, setCurrentSubtitle } = useSubtitle()
  const { play, pause } = useControl()

  // Functions
  const getChapter = useCallback((chapter: number) => {
    const chapter0 = chapter - 1 // Decrement to convert to 0-based index
    if (chapter0 >= 0 && chapter0 < videos.length) {
      return videos[chapter0] as VideoChapter
    }

    return {
      id: '',
      title: '',
      englishAudio: '',
      maoriAudio: '',
      englishSubtitle: '',
      maoriSubtitle: '',
      hasHotspots: false,
      // Default empty chapter if out of bounds
    } as VideoChapter
  }, [])

  const getCurrentChapter = useCallback(() => {
    return getChapter(state.currentChapterNumber)
  }, [state.currentChapterNumber, getChapter])

  const setCurrentChapter = useCallback(
    (chapter: number, autoPlay = true) => {
      if (chapter > 0 && chapter <= videos.length) {
        // Always disable hotspots when navigating chapters
        setState((prev) => ({ ...prev, showHotspots: false, currentChapterNumber: chapter, isChangingChapter: false }))

        console.log(`Setting current chapter to: ${chapter}`, 'navigation')

        // Emit chapter change event
        window.dispatchEvent(new CustomEvent('chapter:change', { detail: chapter }))

        // NB: Chapter numbers are 1-based, so we need to adjust for 0-based indexing in the videos array
        const chapterObj = videos[chapter - 1]
        setCurrentVideo(chapterObj)

        // Clear current subtitle text immediately to prevent showing previous chapter's subtitles
        setCurrentSubtitle(null)

        // Set subtitles
        if (userPrefs.subtitlesEnabled) {
          const subtitleFile =
            (userPrefs.language === 'maori' ? chapterObj.maoriSubtitle : chapterObj.englishSubtitle) ?? null
          setSubtitleFile(subtitleFile)
          setEnabled(true)
        } else {
          setSubtitleFile(null)
          setEnabled(false)
        }

        // Auto-play by default unless explicitly disabled (e.g., when navigating in chapter menu)
        // This ensures video starts for: initial load, auto-advance, and explicit chapter selection
        if (autoPlay && !state.showChapterMenu) {
          play()
        }
        // Audio will be handled by AudioElement based on chapter and overlay state

        // Start intelligent chapter preloading
        startChapterPreloading(chapterObj)
      }
    },
    [setCurrentVideo, setSubtitleFile, setEnabled, setCurrentSubtitle, userPrefs, play, state.showChapterMenu]
  )

  const nextChapter = useCallback(() => {
    const nextChapter = state.currentChapterNumber + 1
    if (nextChapter <= videos.length) {
      setCurrentChapter(nextChapter)
    }
  }, [state.currentChapterNumber, setCurrentChapter])

  const previousChapter = useCallback(() => {
    const prevChapter = state.currentChapterNumber - 1
    if (prevChapter > 0) {
      setCurrentChapter(prevChapter)
    }
  }, [state.currentChapterNumber, setCurrentChapter])

  const setShowChapterMenu = useCallback(
    (show = true, controlVideo = true) => {
      if (controlVideo) {
        if (show) {
          // Pause video
          pause()
        } else {
          play()
        }
      }
      setState((prev) => ({ ...prev, showChapterMenu: show }))
    },
    [play, pause]
  )

  // New: Setters for isChangingChapter and showHotspots
  const setIsChangingChapter = useCallback((changing: boolean) => {
    setState((prev) => ({ ...prev, isChangingChapter: changing }))
  }, [])

  const setShowHotspots = useCallback((show: boolean) => {
    setState((prev) => ({ ...prev, showHotspots: show }))
  }, [])

  // Intelligent chapter preloading function
  const startChapterPreloading = useCallback(
    (currentChapter: VideoChapter) => {
      // Only start preloading if codec detection is complete to ensure correct video format
      if (!userPrefs.codecDetectionComplete) {
        console.log(['Skipping preload - codec detection not complete yet'], 'preload')
        return
      }

      // Only start preloading if service worker is available
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'START_CHAPTER_PRELOADING',
          payload: {
            currentChapter,
            chapters: videos,
            userPrefs: {
              isMobile: userPrefs.isMobile,
              useAV1: userPrefs.useAV1,
              isIOS:
                /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document),
              subtitlesEnabled: userPrefs.subtitlesEnabled,
              language: userPrefs.language,
            },
          },
        })
      }
    },
    [userPrefs]
  )

  // Track if we've already triggered initial preloading after codec detection
  const hasTriggeredInitialPreload = useRef(false)

  // Trigger preloading once codec detection completes (if we have a current chapter)
  useEffect(() => {
    if (userPrefs.codecDetectionComplete && !hasTriggeredInitialPreload.current && state.currentChapterNumber > 0) {
      hasTriggeredInitialPreload.current = true
      const chapter = getChapter(state.currentChapterNumber)
      if (chapter) {
        console.log(['Codec detection complete, starting chapter preloading'], 'preload')
        startChapterPreloading(chapter)
      }
    }
  }, [userPrefs.codecDetectionComplete, state.currentChapterNumber, getChapter, startChapterPreloading])

  const value = {
    ...state,
    getChapter,
    getCurrentChapter,
    setCurrentChapter,
    nextChapter,
    previousChapter,
    setShowChapterMenu,
    setIsChangingChapter, // new
    setShowHotspots, // new
  }

  return <ChapterContext.Provider value={value}>{children}</ChapterContext.Provider>
}

export function useChapter() {
  const context = useContext(ChapterContext)
  if (context === undefined) {
    throw new Error('useChapter must be used within a ChapterProvider')
  }
  return context
}
